import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { jaJP } from "@mui/material/locale";
import { createTheme } from "@mui/material/styles";

/**
 * MUIのテーマを定義している
 * link: https://www.figma.com/design/nluOXbeVQz0atSzftv1pFL/%F0%9F%92%A0Design-System?node-id=13915-78652&m=dev
 */
export const theme = createTheme(
  {
    typography: {
      fontFamily: ["YuGothic", "Yu Gothic", "ヒラギノ角ゴ ProN"].join(","),
    },
    palette: {
      primary: {
        main: "#014CBE",
      },
      text: {
        primary: "#1C2E2E",
      },
    },
    components: {
      MuiSvgIcon: {
        styleOverrides: {
          colorSecondary: {
            color: "#585f60",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            height: "52px",
            width: "100%",
            padding: "0 16px",
            backgroundColor: "#ffffff",
            color: "#1C2E2E",
            display: "flex",
            flexFlow: "row",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "0px",
            boxShadow: "none",
            borderBottom: "1px solid var(--Stroke-border, #d3d8d8)",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: "#ffffff",
            borderRadius: "0px",
            padding: 0,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: "1.125rem",
            fontWeight: 700,
            lineHeight: "175%",
            letterSpacing: "1px",
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h1: {
            fontSize: "1.5rem",
            fontWeight: 700,
            lineHeight: "32px",
            letterSpacing: "0.5px",
          },
          h2: {
            fontSize: "1.25rem",
            fontWeight: 700,
            lineHeight: "28px",
            letterSpacing: "0.5px",
          },
          h3: {
            // 注意: Figma に対応トークン無し（ttl は h1=24px / h2=20px のみ）。コード独自の 18px 見出し
            fontSize: "1.125rem",
            fontWeight: 700,
            lineHeight: "28px",
            letterSpacing: "0.5px",
          },
          h4: {
            fontSize: "1rem",
            fontWeight: 700,
            lineHeight: "24px",
            letterSpacing: "1px",
          },
          h5: {
            fontSize: "0.875rem",
            fontWeight: 700,
            lineHeight: "20px",
            letterSpacing: "0.5px",
          },
          subtitle1: {
            // Figma: body/body 16 (Medium, lineHeight 24px, letterSpacing 0.5px)
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: "24px",
            letterSpacing: "0.5px",
          },
          subtitle2: {
            // Figma: body/body 14 (Medium, lineHeight 20px, letterSpacing 0.5px)
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: "20px",
            letterSpacing: "0.5px",
          },
          body1: {
            // Figma: body/body 12 (Medium, lineHeight 16px, letterSpacing 0.5px)
            fontSize: "0.75rem",
            fontWeight: 500,
            lineHeight: "16px",
            letterSpacing: "0.5px",
          },
          body2: {
            // Figma: body/body 10 (Bold, lineHeight 16px, letterSpacing 0.5px)
            fontSize: "0.625rem",
            fontWeight: 700,
            lineHeight: "16px",
            letterSpacing: "0.5px",
          },
          button: {
            // 注意: Figma に button 専用トークン無し。body/body 16 bold に準拠（lineHeight は MUI 既定の挙動を優先し 175% を維持）
            fontSize: "1rem",
            fontWeight: 700,
            lineHeight: "175%",
            letterSpacing: "1px",
          },
          caption: {
            fontSize: "0.75rem",
            fontWeight: 700,
            lineHeight: "16px",
            letterSpacing: "0.5px",
            // 注意: #9D9D9D はデザイントークン外の灰色（Figma の Text/subtle は #585f60）
            color: "#9D9D9D",
            display: "block",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            background: "linear-gradient(180deg, #0058de 0%, #00398f 100%)",
            color: "#FFFFFF",
            borderRadius: "999px",
            fontWeight: "bold",
            "&:hover": {
              background: "linear-gradient(180deg, #0058de 0%, #00398f 100%)",
              opacity: "0.96",
            },
          },
          containedSecondary: {
            background: "#FFFFFF",
            color: "#1C2E2E",
            borderRadius: "999px",
            border: "1px solid #014CBE",
            fontWeight: "bold",
            "&:hover": {
              background: "#e7edf7",
              opacity: "0.96",
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            padding: 0,
            marginBottom: "8px",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            padding: 0,
            "&:hover": {
              background: "rgba(1, 76, 190, 0.04)",
            },
            "&:focus": {
              background: "#ffffff",
            },
            "> .MuiTouchRipple-root span": {
              background: "rgba(1, 76, 190, 0.04)",
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: "0px",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            backgroundColor: "#fff",
            padding: "24px",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            backgroundColor: "#fff",
            padding: 0,
            // Figma: Stroke/border
            border: "1px solid #d3d8d8",
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          root: {
            "&:hover": {
              background:
                "linear-gradient(0deg, rgba(28, 46, 46, 0.50) 0%,  rgba(28, 46, 46, 0.50) 100%)",
            },
            "&:active": {
              background:
                "linear-gradient(0deg, rgba(28, 46, 46, 0.50) 0%,  rgba(28, 46, 46, 0.50) 100%)",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            // Figma: Neutral/700 (Text/subtle)
            color: "#585f60",
          },
          formControl: {
            position: "static",
            transform: "none",
            transition: "none",
            pointerEvents: "auto",
            cursor: "pointer",
            display: "inline",
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: "0",
            marginRight: "16px",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            marginTop: 0,
            backgroundColor: "#ffffff",
            height: "50px",
          },
          input: {
            height: "auto",
          },
          notchedOutline: {
            top: 0,
            legend: {
              display: "none",
            },
          },
          multiline: {
            height: "auto",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            // Figma: Neutral/700 (Text/subtle)
            color: "#585f60",
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          IconComponent: KeyboardArrowDownIcon,
        },
        styleOverrides: {
          root: {
            backgroundColor: "#F7F9FC",
            borderRadius: "8px",
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: "white",
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: "1rem",
            fontWeight: 400,
            lineHeight: "175%",
            letterSpacing: "1px",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          colorError: {
            // 注意: Figma に赤系のデザイントークンは未定義。コード独自の値
            backgroundColor: "#ee4f4f",
            color: "#ffffff",
            fontWeight: "bold",
          },
          colorWarning: {
            // Figma: Secondary/default
            backgroundColor: "#ffb340",
            color: "#ffffff",
            fontWeight: "bold",
          },
          colorInfo: {
            // Figma: 背景は Secondary/light。文字色 #663D00 は対応トークン無し（可読性確保のためのペア色）
            backgroundColor: "#FFEBCC",
            color: "#663D00",
            fontWeight: "bold",
          },
          root: {
            // Figma: Neutral/700 (Text/subtle)
            backgroundColor: "#585f60",
            color: "#ffffff",
            fontWeight: "bold",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: "#F7F9FC",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:nth-of-type(even)": {
              backgroundColor: "#F7F9FC",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
            fontWeight: 700,
            lineHeight: "175%",
            letterSpacing: "1px",
            padding: "16px 24px",
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            marginTop: "12px !important",
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            background: "rgba(28, 46, 46, 0.50)",
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            padding: "4px 24px",
          },
          filledWarning: {
            backgroundColor: "#FFB340",
          },
          filledError: {
            // 注意: Figma に赤系のデザイントークンは未定義。コード独自の値
            backgroundColor: "#EE4F4F",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: "#575D64",
            "&.Mui-checked": {
              color: "#fff",
            },
          },
        },
      },
    },
  },
  jaJP,
);
